import React, { useState } from 'react';
import { Form, Input, DatePicker, Button, Row, Col, Space, Tooltip } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { creerLot } from '../../../services/attestationsService';
import CBModal from '../../../components/CBModal';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';

export default function NouveauLotModal({ open, onClose, onCreated }) {
  const { t } = useTranslation('attestations');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const calcQuantite = (debut, fin) => {
    const d = parseInt(String(debut).trim(), 10);
    const f = parseInt(String(fin).trim(), 10);
    if (!Number.isFinite(d) || !Number.isFinite(f) || f < d) return null;
    return f - d + 1;
  };

  const onFinish = async (vals) => {
    const plages = (vals.plages || []).filter(p => p && p.numeroDebutSerie && p.numeroFinSerie);
    if (plages.length === 0) {
      toast.error(t('modales.nouveauLot.auMoinsUnePlage'));
      return;
    }
    // Validation : quantité positive et plages non chevauchantes
    const ranges = [];
    for (const p of plages) {
      const q = calcQuantite(p.numeroDebutSerie, p.numeroFinSerie);
      if (q == null || q <= 0) {
        toast.error(t('modales.nouveauLot.plageInvalide', { debut: p.numeroDebutSerie, fin: p.numeroFinSerie }));
        return;
      }
      const d = parseInt(p.numeroDebutSerie, 10);
      const f = parseInt(p.numeroFinSerie, 10);
      for (const r of ranges) {
        if (!(f < r.d || d > r.f)) {
          toast.error(t('modales.nouveauLot.chevauchement', { debut: p.numeroDebutSerie, fin: p.numeroFinSerie }));
          return;
        }
      }
      ranges.push({ d, f });
    }

    setSubmitting(true);
    try {
      const dateCommande = vals.dateCommande?.format('YYYY-MM-DD') || null;
      const dateLivraisonFournisseur = vals.dateLivraisonFournisseur?.format('YYYY-MM-DD') || null;
      const nomFournisseur = vals.nomFournisseur?.trim() || null;
      const numeroBonCommande = vals.numeroBonCommande?.trim() || null;

      /* UN LOT APRÈS L'AUTRE, et non tous en même temps.

         Le serveur forge la référence par « MAX + 1 » : lancés en parallèle,
         deux enregistrements lisaient le même maximum, forgeaient la même
         référence, et l'index unique n'en laissait passer qu'un. On saisissait
         cinq plages, une seule était prise, et le reste tombait en erreur.

         Le serveur est désormais protégé par un verrou - deux agents qui
         saisissent au même instant ne se marchent plus dessus. Mais envoyer en
         série reste juste : les lots partent dans l'ordre où l'agent les a
         écrits, leurs références se suivent, et un échec nomme la bonne plage
         au lieu d'un rang dans une liste de promesses. */
      const ok = [];
      const ko = [];
      for (const p of plages) {
        try {
          const lot = await creerLot({
            referenceLot: null,
            nomFournisseur,
            numeroBonCommande,
            quantite: calcQuantite(p.numeroDebutSerie, p.numeroFinSerie),
            numeroDebutSerie: String(p.numeroDebutSerie).trim(),
            numeroFinSerie: String(p.numeroFinSerie).trim(),
            prixUnitaireAchat: null,
            dateCommande,
            dateLivraisonFournisseur,
          });
          ok.push(lot);
        } catch (e) {
          ko.push({ plage: p, erreur: e });
        }
      }

      if (ok.length > 0) toast.success(t('modales.nouveauLot.enregistres', { count: ok.length }));
      // La PLAGE en échec, et non son rang : « lot 3 » n'aide personne à
      // retrouver la ligne à corriger dans un formulaire de six plages.
      ko.forEach(({ plage, erreur }) => {
        const reason = erreur?.response?.data?.message
          || erreur?.response?.data?.error
          || erreur?.message
          || t('modales.nouveauLot.erreurInconnue');
        toast.error(t('modales.nouveauLot.echec', { debut: plage.numeroDebutSerie, fin: plage.numeroFinSerie, raison: reason }));
      });

      if (ok.length > 0) {
        form.resetFields();
        onCreated?.(ok);
        if (ko.length === 0) onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CBModal title={t('modales.nouveauLot.titre')}
      open={open}
      onCancel={() => { form.resetFields(); onClose?.(); }}
      footer={null}
      width={760}
      destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish}
        initialValues={{ plages: [{}] }}>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="nomFournisseur" label={t('commun.lot.fournisseur')}>
              <Input placeholder={t('commun.lot.fournisseurPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="numeroBonCommande" label={t('commun.lot.bonCommande')}>
              <Input placeholder={t('commun.optionnel')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="dateCommande" label={t('modales.nouveauLot.dateCommande')}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="dateLivraisonFournisseur" label={t('modales.nouveauLot.dateLivraisonFournisseur')}>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginBottom: 8 }}>
          <strong style={{ color: '#1a6b3c' }}>{t('modales.nouveauLot.plages')}</strong>
          <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
            {t('modales.nouveauLot.plagesAide')}
          </span>
        </div>

        <Form.List name="plages">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Row gutter={12} key={field.key} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={1}>
                    <span style={{ color: '#6b7280', fontWeight: 600 }}>{index + 1}</span>
                  </Col>
                  <Col span={9}>
                    <Form.Item {...field} key={`${field.key}-d`} name={[field.name, 'numeroDebutSerie']}
                      rules={[{ required: true, message: t('modales.nouveauLot.debutRequis') }]}
                      style={{ marginBottom: 0 }}>
                      <Input placeholder={t('modales.nouveauLot.debutPlaceholder')} />
                    </Form.Item>
                  </Col>
                  <Col span={9}>
                    <Form.Item {...field} key={`${field.key}-f`} name={[field.name, 'numeroFinSerie']}
                      rules={[{ required: true, message: t('modales.nouveauLot.finRequis') }]}
                      style={{ marginBottom: 0 }}>
                      <Input placeholder={t('modales.nouveauLot.finPlaceholder')} />
                    </Form.Item>
                  </Col>
                  <Col span={3} style={{ fontSize: 12, color: '#1a6b3c' }}>
                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const p = form.getFieldValue(['plages', field.name]) || {};
                        const q = calcQuantite(p.numeroDebutSerie, p.numeroFinSerie);
                        return q != null
                          ? <span><strong>{q.toLocaleString(localeIntl())}</strong> {t('modales.nouveauLot.abreviation')}</span>
                          : <span style={{ color: '#9ca3af' }}>-</span>;
                      }}
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ textAlign: 'right' }}>
                    {fields.length > 1 && (
                      <Tooltip title={t('modales.nouveauLot.retirer')}>
                        <Button type="text" danger icon={<MinusCircleOutlined />}
                          onClick={() => remove(field.name)} />
                      </Tooltip>
                    )}
                  </Col>
                </Row>
              ))}
              <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}
                style={{ width: '100%', marginTop: 4 }}>
                {t('modales.nouveauLot.ajouter')}
              </Button>
            </>
          )}
        </Form.List>

        <Space style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 16 }}>
          <Button onClick={() => { form.resetFields(); onClose?.(); }}>{t('commun.annuler')}</Button>
          <Button type="primary" htmlType="submit" loading={submitting}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('commun.enregistrer')}
          </Button>
        </Space>
      </Form>
    </CBModal>
  );
}
